import lineIntersect from '@turf/line-intersect';
import { lineString as asTurfLineString } from '@turf/helpers';
import { equals } from 'ol/coordinate';

import type { Oval } from 'cadenza/map/geom/oval';
import type { Rectangle } from 'cadenza/map/geom/rectangle';

import type { Coordinate } from 'ol/coordinate';
import type Polygon from 'ol/geom/Polygon';
import type { FeatureCollection } from 'geojson';

export function isOLDrawSnapToFinish (polygon: Polygon) {
  const coords = polygon.getCoordinates()[0];
  // ol/draw will not offer snapping
  if (coords.length < 5) {
    return false;
  }

  const drawCoordIndex = coords.length - 1;
  // checks if the current draw coord is snapped to finish coords.
  const drawCoord = coords[drawCoordIndex - 1];
  const prevCoord = coords[drawCoordIndex - 2];
  const nextCoord = coords[drawCoordIndex];

  return equals(drawCoord, prevCoord) || equals(drawCoord, nextCoord);
}

export function isPolygonWithoutDrawCoordValidToFinish (polygon: Polygon): boolean {
  const coords = polygon.getCoordinates()[0];
  // Without the draw coord, the polygon must contain at least 4 coords in order to be valid.
  if (coords.length < 5) {
    return false;
  }
  // If the polygon without the draw coord has 4 coords, then it is a triangle and thus has no intersections.
  if (coords.length === 5) {
    return true;
  }
  // remove the draw coord
  coords.splice(-2, 1);
  // get the index of the latest drawn coord, which is the point we use to finish the polygon
  const latestDrawnCoordIndex = coords.length - 2;
  // build the line from the latest drawn coord to the start vertex of the polygon
  const lineFromLatestDrawnToFinish = asTurfLineString(coords.slice(latestDrawnCoordIndex, latestDrawnCoordIndex + 2));
  // we test against the other lines of the polygon.
  // Since the first and last edge of the array are connected to the finish line,
  // turf creates always one intersection. So we exclude the first and last vertex of the coord array.
  const otherLinesOfPolygon = asTurfLineString(coords.slice(1, latestDrawnCoordIndex));
  return hasNoIntersections(lineIntersect(lineFromLatestDrawnToFinish, otherLinesOfPolygon));
}

export function isPolygonWithDrawCoordValidToFinish (polygon: Polygon): boolean {
  const coords = polygon.getCoordinates()[0];
  // the draw.js polygon needs to have at least 4 coords to be finish-able,
  if (coords.length < 4) {
    return false;
  }
  // get the index of the current draw coord, which is the point to check for validation
  const drawCoordIndex = coords.length - 2;
  const lineFromLatestToCurrentDrawCoord = asTurfLineString(coords.slice(drawCoordIndex - 1, drawCoordIndex + 1));
  const lineFromCurrentDrawCoordToFinish = asTurfLineString(coords.slice(drawCoordIndex, drawCoordIndex + 2));

  const otherLinesOfPolygon = coords.slice(0, drawCoordIndex);
  // turf always reports intersections on the first and last edge,
  // since we have duplicated vertices at the ends of the line strings.
  // This is why we exclude the connecting edge for each intersection test.
  const intersection1 = lineIntersect(lineFromLatestToCurrentDrawCoord, asTurfLineString(otherLinesOfPolygon.slice(0, -1)));
  const intersection2 = lineIntersect(lineFromCurrentDrawCoordToFinish, asTurfLineString(otherLinesOfPolygon.slice(1)));
  return hasNoIntersections(intersection1) && hasNoIntersections(intersection2);
}

export function isPolygonDrawCoordValidToDraw (polygon: Polygon): boolean {
  const coords = polygon.getCoordinates()[0];
  // This happens if the user clicks twice at the start of drawing.
  // Reason: Since in between those clicks was no move event, ol did not create the draw coordinate yet.
  if (coords.length === 2) {
    return false;
  }
  const drawCoordIndex = coords.length - 2;
  // Until the polygon becomes a triangle, we allow to draw.
  // We also check for a duplicate here. Later, this check is included via the turf intersection test.
  if (coords.length < 5) {
    return !containsCoord(coords.slice(0, drawCoordIndex), coords[drawCoordIndex]);
  }
  const lineFromLatestToCurrentDrawCoord = asTurfLineString(coords.slice(drawCoordIndex - 1, drawCoordIndex + 1));
  const otherLinesOfPolygon = asTurfLineString(coords.slice(0, drawCoordIndex - 1));
  return hasNoIntersections(lineIntersect(lineFromLatestToCurrentDrawCoord, otherLinesOfPolygon));
}

function hasNoIntersections (intersections: FeatureCollection) {
  return intersections.features.length === 0;
}

export function containsCoord (coords: Coordinate[], toCheck: Coordinate): boolean {
  return coords && coords.some(coord => equals(coord, toCheck));
}

export function isOvalDrawCoordValidToDraw (oval: Oval): boolean {
  return oval.isValid();
}

export function isRectangleDrawCoordValidToDraw (rectangle: Rectangle): boolean {
  return rectangle.isValid();
}
